/*************************************************************************
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React, { memo } from "react";
import { IntlProvider, injectIntl } from "react-intl";
import PropTypes from "prop-types";
import { locale2 as localeAPI } from "dc-core";

// load translation strings and also load locale data if required
const loadTranslations = translationsCb => localeParam => {
  let locale = localeAPI.normalizeLocale(localeParam);
  if (!locale) locale = "en-US"; // default locale
  return translationsCb(locale); // incase of error return empty translations
};

const wrapOriginalApp = OriginalApp => {
  const InjectedApp = injectIntl(OriginalApp);
  const Wrapped = props => {
    const { messages, ...otherProps } = props;
    const { locale } = props;
    return (
      <IntlProvider defaultLocale={"en-US"} locale={locale} messages={messages}>
        <InjectedApp {...otherProps} />
      </IntlProvider>
    );
  };

  Wrapped.propTypes = {
    locale: PropTypes.string.isRequired,
    messages: PropTypes.objectOf(PropTypes.string)
  };

  Wrapped.defaultProps = {
    messages: {}
  };
  return memo(Wrapped);
};

const localizeComponent = (OriginalApp, translationsCb, options) =>
  localeAPI.withTranslations(
    wrapOriginalApp(OriginalApp),
    loadTranslations(translationsCb),
    options
  );

export default localizeComponent;
